import { ExportDatasetInfo, ExportDatasetWidgetInfo, ExportFormat } from '@ddv/models';

import { ColumnDataMapping, MappedMetadata } from '../models/data-mapping';
import { GroupMetadata } from '../models/group-metadata';

export function getMappedMetadata(dataset: ExportDatasetInfo): { name: string, displayName: string }[] {
    if (!dataset.metadata) {
        return [];
    }

    return Object.values(dataset.metadata).map(({ name, displayName }) => ({
        name: name ?? '',
        displayName: displayName ?? '',
    }));
}

export function getColumnData(data: Record<string, unknown>, columnNames: string[], mappedMetadata: MappedMetadata[]): ColumnDataMapping[] {
    const datasetKeys = Object.keys(data);

    return columnNames.map((name) => {
        const columnName = mappedMetadata.find((md) => md.name === name || md.displayName === name)?.displayName ?? name;
        const dataKey = findDatasetKey(datasetKeys, name) ?? findMetadataKey(mappedMetadata, name);

        return { columnName, dataKey };
    });
}

export function getRowData(
    dataset: ExportDatasetInfo,
    grouperData: ColumnDataMapping[],
    columnData: ColumnDataMapping[],
    exportFormat: ExportFormat,
    columns: string[],
): Record<string, unknown>[] {
    return dataset.data.map((content) => {
        const mappedData = mapContentToColumnData([...grouperData, ...columnData], content, exportFormat);
        return exportFormat === 'EXCEL' ?
            { ...mappedData, ...populateGroupValues(content) } :
            { ...mappedData, ...populateDateValues(dataset.widgets[0], columns) };
    });
}

export function createSummaryRow(columnData: ColumnDataMapping[], dataset: ExportDatasetInfo): Record<string, unknown> {
    return columnData.reduce((summaryRow: Record<string, unknown>, d) => {
        if (d.dataKey) {
            summaryRow[d.columnName] = dataset.summary?.[d.dataKey] ?? '';
        }
        return summaryRow;
    }, {});
}

function findDatasetKey(datasetKeys: string[], name: string): string | undefined {
    return datasetKeys.find((key) => key === name);
}

function findMetadataKey(mappedMetadata: MappedMetadata[], name: string): string | undefined {
    return mappedMetadata.find((md) => md.name === name || md.displayName === name)?.name;
}

function mapContentToColumnData(
    columnData: ColumnDataMapping[],
    content: Record<string, unknown>,
    exportFormat: ExportFormat,
): Record<string, unknown> {
    return columnData.reduce((mappedData: Record<string, unknown>, d) => {
        if (d.dataKey) {
            const key = exportFormat === 'CSV' ?
                d.columnName.replace('_crosstalk_key', '') :
                d.columnName;
            mappedData[key] = content[d.dataKey];
        }
        return mappedData;
    }, {});
}

function populateGroupValues(data: Record<string, unknown>): GroupMetadata {
    if (!Object.prototype.hasOwnProperty.call(data, '_row_id')) {
        return {};
    }
    return {
        agGroup: data._group,
        parentId: data._parent_id,
        rowId: data._row_id,
    };
}

function populateDateValues(widgetInfo: ExportDatasetWidgetInfo, columns: string[]): Record<string, unknown> {
    const dateData = {} as Record<string, unknown>;
    if (columns.includes('From Date')) {
        dateData['From Date'] = widgetInfo.dateFrom;
    }
    if (columns.includes('To Date')) {
        dateData['To Date'] = widgetInfo.dateTo;
    }
    if (columns.includes('Time of Extract')) {
        dateData['Time of Extract'] = widgetInfo.extractionDateTime;
    }
    return dateData;
}
